import React, { useState, useMemo, useEffect } from 'react';
import toast from 'react-hot-toast';

const AssignFeeStructureModal = ({ feeStructures, onClose, onSubmit, students, allClasses, feeRecords, fetchStudentsForClasses, preSelectedClassId }) => {
  const [step, setStep] = useState(1); // 1: Select Structure, 2: Select Classes, 3: Select Students
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [selectedClasses, setSelectedClasses] = useState([]); // Array of class objects
  const [selectedStudents, setSelectedStudents] = useState([]); // Array of student _id's
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);

  // Filter fee structures based on pre-selected class
  const filteredFeeStructures = useMemo(() => {
    if (!preSelectedClassId || typeof preSelectedClassId !== 'string') return feeStructures;
    return feeStructures.filter(fs => fs.applicableClassIds?.includes(preSelectedClassId));
  }, [feeStructures, preSelectedClassId]);

  // Get applicable classes from the selected structure (Filtering all classes by IDs)
  const applicableClasses = useMemo(() => {
    return selectedStructure 
      ? allClasses.filter(cls => selectedStructure.applicableClassIds?.includes(cls._id))
      : [];
  }, [selectedStructure, allClasses]);

  // Get students from all selected classes (Filtering all students by selected class IDs)
  const allStudentsFromSelectedClasses = useMemo(() => {
    const selectedClassIds = selectedClasses.map(c => c._id);
    return students.filter(s => {
        // Handle both object and string for unitId
        const studentClassId = s.enrollment?.unitId?._id || s.enrollment?.unitId || s.unitId?._id || s.unitId;
        return selectedClassIds.includes(studentClassId);
    });
  }, [students, selectedClasses]);

  const handleClassToggle = (classId) => {
    setSelectedClasses(prev => {
      const exists = prev.find(c => c._id === classId);
      if (exists) {
        return prev.filter(c => c._id !== classId);
      } else {
        const classObj = allClasses.find(c => c._id === classId);
        return classObj ? [...prev, classObj] : prev;
      }
    });
    // Clear selected students when classes change
    setSelectedStudents([]);
  };

  const handleStudentToggle = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAllStudents = () => {
    const allStudentIds = allStudentsFromSelectedClasses.map(s => s._id);
    if (selectedStudents.length === allStudentIds.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(allStudentIds);
    }
  };

  const handleNext = () => {
    if (step === 1 && !selectedStructure) {
      toast.error('Please select a fee structure');
      return;
    }
    if (step === 2 && selectedClasses.length === 0) {
      toast.error('Please select at least one class');
      return;
    }
    setStep(prev => prev + 1);
  };

  const handleNextStep2 = async () => {
      if (selectedClasses.length === 0) {
        toast.error('Please select at least one class');
        return;
      }
      
      setIsLoadingStudents(true);
      try {
          // Fetch students for selected classes using the structure's academic year
          await fetchStudentsForClasses(selectedClasses.map(c => c._id), selectedStructure.academicYear);
          setStep(3);
      } catch (e) {
          toast.error("Failed to load students");
      } finally {
          setIsLoadingStudents(false);
      }
  };

  const handleBack = () => {
    if (step === 1) return;
    setStep(prev => prev - 1);
    // Logic to reset state on step back if needed
    if (step === 2) setSelectedStructure(null);
    if (step === 3) setSelectedStudents([]);
  };

  const handleSubmit = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }

    // Group selected students by class (using the enrollment ID if available)
    const groupedByClass = selectedClasses
      .map((cls) => {
        const studentEnrollmentIds = students
          .filter((s) => {
            const classId = s.enrollment?.unitId?._id || s.enrollment?.unitId || s.unitId?._id || s.unitId;
            return classId === cls._id && selectedStudents.includes(s._id);
          })
          .map((s) => s.enrollment?._id || s.enrollmentId) // Strictly use enrollment ID
          .filter(Boolean); 

        return {
          classId: cls._id,
          studentEnrollmentIds: studentEnrollmentIds, // Use studentEnrollmentIds as expected by backend
        };
      })
      .filter((c) => c.studentEnrollmentIds.length > 0);

    if (groupedByClass.length === 0) {
      toast.error('No students found to assign the structure to in the selected classes.');
      return;
    }

    await onSubmit({
      feeStructureId: selectedStructure._id,
      classes: groupedByClass,
    });
  };

  // Effect to handle state transition/reset on structure change
  useEffect(() => {
    if (selectedStructure) {
       // Pre-select class if provided
       if (preSelectedClassId) {
           const cls = allClasses.find(c => c._id === preSelectedClassId);
           if (cls && selectedStructure.applicableClassIds?.includes(cls._id)) {
               setSelectedClasses([cls]);
               setStep(2); // Jump to step 2 if pre-selected
           }
       }
    }
  }, [selectedStructure, preSelectedClassId, allClasses]);

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-3 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t">
          <h3 className="text-base font-bold text-gray-900">Assign Fee Structure</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <span className="sr-only">Close</span>
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="p-4">
          {/* Steps Indicator */}
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center" style={{ color: step >= 1 ? 'var(--primary-color, #4f46e5)' : '#9ca3af' }}>
              <div className="w-6 h-6 rounded-full flex items-center justify-center border-2 text-xs font-bold" style={step >= 1 ? { borderColor: 'var(--primary-color, #4f46e5)', backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)' } : { borderColor: '#d1d5db' }}>1</div>
              <span className="ml-2 text-xs font-medium">Structure</span>
            </div>
            <div className="w-8 h-0.5 mx-3" style={{ backgroundColor: step >= 2 ? 'var(--primary-color, #4f46e5)' : '#d1d5db' }}></div>
            <div className="flex items-center" style={{ color: step >= 2 ? 'var(--primary-color, #4f46e5)' : '#9ca3af' }}>
              <div className="w-6 h-6 rounded-full flex items-center justify-center border-2 text-xs font-bold" style={step >= 2 ? { borderColor: 'var(--primary-color, #4f46e5)', backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)' } : { borderColor: '#d1d5db' }}>2</div>
              <span className="ml-2 text-xs font-medium">Classes</span>
            </div>
            <div className="w-8 h-0.5 mx-3" style={{ backgroundColor: step >= 3 ? 'var(--primary-color, #4f46e5)' : '#d1d5db' }}></div>
            <div className="flex items-center" style={{ color: step >= 3 ? 'var(--primary-color, #4f46e5)' : '#9ca3af' }}>
              <div className="w-6 h-6 rounded-full flex items-center justify-center border-2 text-xs font-bold" style={step >= 3 ? { borderColor: 'var(--primary-color, #4f46e5)', backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)' } : { borderColor: '#d1d5db' }}>3</div>
              <span className="ml-2 text-xs font-medium">Students</span>
            </div>
          </div>

          {/* Step 1: Select Structure */}
          {step === 1 && (
            <div className="space-y-3">
              <label className="block text-xs font-bold text-gray-700">Select Fee Structure</label>
              <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
                {filteredFeeStructures.filter(fs => fs.isActive).map(structure => (
                  <div 
                    key={structure._id}
                    onClick={() => setSelectedStructure(structure)}
                    className="cursor-pointer p-3 rounded border transition-all"
                    style={selectedStructure?._id === structure._id ? {
                      borderColor: 'var(--primary-color, #4f46e5)',
                      backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)'
                    } : { borderColor: '#e5e7eb' }}
                    onMouseOver={(e) => !selectedStructure || selectedStructure._id !== structure._id ? e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--primary-color, #4f46e5) 40%, white)' : null}
                    onMouseOut={(e) => !selectedStructure || selectedStructure._id !== structure._id ? e.currentTarget.style.borderColor = '#e5e7eb' : null}
                  >
                    <div className="font-bold text-sm text-gray-900">{structure.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{structure.academicYear}</div>
                    <div className="text-[10px] text-gray-400 mt-1">{structure.categories?.length || 0} categories</div>
                  </div>
                ))}
                {filteredFeeStructures.filter(fs => fs.isActive).length === 0 && (
                  <div className="col-span-full py-6 text-center text-xs text-gray-500 bg-gray-50 rounded">
                    No active fee structures found for this selection.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Select Classes */}
          {step === 2 && (
            <div className="space-y-3">
              <label className="block text-xs font-bold text-gray-700">Select Classes</label>
              <p className="text-xs text-gray-500">Select classes to assign <strong>{selectedStructure?.name}</strong> to.</p>
              {applicableClasses.length === 0 ? (
                <div className="text-center py-6 text-xs text-gray-500 bg-gray-50 rounded">
                  No applicable classes found for this structure.
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {applicableClasses.map(cls => (
                    <div 
                      key={cls._id}
                      onClick={() => handleClassToggle(cls._id)}
                      className="cursor-pointer p-2 rounded border text-center text-xs font-medium transition-all"
                      style={selectedClasses.find(c => c._id === cls._id) ? {
                        borderColor: 'var(--primary-color, #4f46e5)',
                        backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)',
                        color: 'var(--primary-color, #4f46e5)'
                      } : { borderColor: '#e5e7eb' }}
                    >
                      {cls.unitName}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Select Students */}
          {step === 3 && (
            <div className="space-y-3">
               <div className="flex justify-between items-center">
                  <label className="block text-xs font-bold text-gray-700">Select Students</label>
                  <button onClick={handleSelectAllStudents} className="text-xs font-medium rounded px-2 py-1" style={{ color: 'var(--primary-color, #4f46e5)', backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--primary-color, #4f46e5) 15%, white)'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)'}>
                    {selectedStudents.length === allStudentsFromSelectedClasses.length ? 'Deselect All' : 'Select All'}
                  </button>
               </div>
               
               {isLoadingStudents ? (
                   <div className="text-center py-8 text-xs text-gray-500">Loading students...</div>
               ) : allStudentsFromSelectedClasses.length === 0 ? (
                   <div className="text-center py-8 text-xs text-gray-500 bg-gray-50 rounded">No students found in selected classes.</div>
               ) : (
                   <div className="max-h-64 overflow-y-auto border border-gray-200 rounded divide-y divide-gray-200">
                       {allStudentsFromSelectedClasses.map(student => (
                           <div key={student._id} className="p-2 flex items-center hover:bg-gray-50 cursor-pointer" onClick={() => handleStudentToggle(student._id)}>
                               <input
                                  type="checkbox"
                                  checked={selectedStudents.includes(student._id)}
                                  onChange={() => {}} // Handled by parent div click
                                   className="h-3.5 w-3.5 border-gray-300 rounded cursor-pointer"
                                   style={{ accentColor: 'var(--primary-color, #4f46e5)' }}
                               />
                               <div className="ml-3">
                                   <div className="text-xs font-bold text-gray-900">{student.firstName} {student.lastName}</div>
                                   <div className="text-[10px] text-gray-500">
                                       {student.enrollment?.unitId?.unitName || student.unitId?.unitName || 'Unknown Class'}
                                   </div>
                               </div>
                           </div>
                       ))}
                   </div>
               )}
               <div className="text-xs text-gray-500 text-right">
                   {selectedStudents.length} students selected
               </div>
            </div>
          )}

        </div>
        
        <div className="p-3 border-t border-gray-200 flex justify-between bg-gray-50 rounded-b">
          <button
            onClick={handleBack}
            disabled={step === 1}
            className={`px-3 py-1.5 border border-gray-300 rounded text-xs font-medium text-gray-700 hover:bg-gray-50 ${step === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Back
          </button>
          
          {step < 3 ? (
            <button
              onClick={step === 1 ? handleNext : handleNextStep2}
              className="btn-primary text-xs"
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              className="btn-primary text-xs"
            >
              Assign Structure
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignFeeStructureModal;
